import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Avatar, Modal, Table, Typography } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import {
    deleteSavedWorkloadReport,
    downloadSavedWorkloadPdf,
    getSavedWorkloadReport,
    updateSavedWorkloadReport,
} from './workload.service'
import {
    formatLogDuration,
    formatLogDurationDetailed,
    formatReportingDisplayDate,
    getInitials,
} from './workload.utils'
import { loginState } from '../../login/login.slice'
import { getReportsAccess } from '../../../helpers/reportsPermission'
import { showActionNotification } from '../../../components/notification'
import { PRIVATE_ROUTES } from '../../../routes'
import StoryMapDeleteConfirm from '../../projects/components/ProjectStoryMapBoard/components/StoryMapDeleteConfirm'

import styles from './SavedReportsPage.module.css'

const { Title, Text } = Typography

const SavedReportDetailPage = () => {
    const { reportId } = useParams()
    const navigate = useNavigate()
    const { userProfile } = useSelector(loginState)
    const reportsAccess = useMemo(
        () => getReportsAccess(userProfile),
        [userProfile]
    )

    const [report, setReport] = useState(null)
    const [status, setStatus] = useState('loading')
    const [error, setError] = useState('')
    const [downloading, setDownloading] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [expandedUserId, setExpandedUserId] = useState(null)
    const [deleteOpen, setDeleteOpen] = useState(false)

    const loadReport = useCallback(async () => {
        if (!reportId) return
        setStatus('loading')
        setError('')
        try {
            const resp = await getSavedWorkloadReport(reportId)
            const data = resp?.data?.data ?? resp?.data ?? null
            setReport(data)
            setStatus('loaded')
        } catch (err) {
            setStatus('error')
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load report'
            )
        }
    }, [reportId])

    useEffect(() => {
        loadReport()
    }, [loadReport])

    const snapshot = report?.snapshot
    const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : []

    const handleDownload = async () => {
        if (!reportId || downloading) return
        setDownloading(true)
        const result = await downloadSavedWorkloadPdf(reportId)
        setDownloading(false)
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to download PDF',
            })
        }
    }

    const handleRefresh = () => {
        if (!reportId || !reportsAccess.canEdit) return
        Modal.confirm({
            title: 'Refresh snapshot?',
            content:
                'This re-runs the original filters against today’s data and replaces the frozen numbers.',
            okText: 'Refresh',
            onOk: async () => {
                setRefreshing(true)
                const result = await updateSavedWorkloadReport(reportId, {
                    resnapshot: true,
                })
                setRefreshing(false)
                if (!result.success) {
                    showActionNotification({
                        type: 'error',
                        message: result.message || 'Failed to refresh report',
                    })
                    return
                }
                showActionNotification({
                    type: 'success',
                    message: result.message || 'Report refreshed',
                })
                if (result.data) setReport(result.data)
                else loadReport()
            },
        })
    }

    const handleDelete = () => {
        if (!reportId || !reportsAccess.canEdit) return
        setDeleteOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (!reportId) return
        const result = await deleteSavedWorkloadReport(reportId)
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to delete report',
            })
            return
        }
        showActionNotification({
            type: 'success',
            message: result.message || 'Report deleted',
        })
        setDeleteOpen(false)
        navigate(PRIVATE_ROUTES.reportingWorkloadSaved)
    }

    const columns = [
        {
            title: 'Team member',
            key: 'name',
            render: (_, record) => (
                <div className={styles.userCell}>
                    <Avatar size={34} src={record?.profilePicture || undefined}>
                        {!record?.profilePicture
                            ? getInitials(record?.name)
                            : null}
                    </Avatar>
                    <div>
                        <div className={styles.userName}>
                            {record?.name || 'Unnamed'}
                        </div>
                        <div className={styles.userEmail}>
                            {record?.email || '—'}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Total logged',
            dataIndex: 'totalLog',
            key: 'totalLog',
            width: 160,
            render: (ms) => formatLogDuration(ms),
        },
        {
            title: 'Projects',
            dataIndex: 'projects',
            key: 'projects',
            render: (projects) => {
                if (!projects?.length) return '—'
                return projects
                    .map((p) => p?.name || 'Project')
                    .slice(0, 4)
                    .join(', ')
            },
        },
    ]

    const rangeLabel = useMemo(() => {
        const start = report?.filters?.start
        const end = report?.filters?.end
        if (!start || !end) return null
        const a = dayjs(start).format('D MMM YYYY')
        const b = dayjs(end).format('D MMM YYYY')
        return a === b ? a : `${a} – ${b}`
    }, [report?.filters?.start, report?.filters?.end])

    const expandedRow = rows.find((r) => r.userId === expandedUserId)

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button
                    type="button"
                    className={styles.backLink}
                    onClick={() =>
                        navigate(PRIVATE_ROUTES.reportingWorkloadSaved)
                    }
                >
                    <Icon icon="mdi:arrow-left" width={18} height={18} />
                    Saved reports
                </button>

                <div className={styles.headerTop}>
                    <div>
                        <Title level={3} className={styles.title}>
                            {report?.name || 'Saved report'}
                        </Title>
                        <p className={styles.subtitle}>
                            {rangeLabel ? `${rangeLabel} · ` : ''}
                            Snapshot
                            {snapshot?.generatedAt
                                ? ` · ${dayjs(snapshot.generatedAt).format(
                                      'D MMM YYYY, h:mm A'
                                  )}`
                                : ''}
                        </p>
                    </div>
                    <div className={styles.detailActions}>
                        {reportsAccess.canView ? (
                            <button
                                type="button"
                                className={styles.actionBtn}
                                disabled={downloading || status === 'loading'}
                                onClick={handleDownload}
                            >
                                <Icon
                                    icon={
                                        downloading
                                            ? 'line-md:loading-loop'
                                            : 'mdi:download-outline'
                                    }
                                    width={16}
                                />
                                Download
                            </button>
                        ) : null}
                        {reportsAccess.canEdit ? (
                            <button
                                type="button"
                                className={styles.actionBtn}
                                disabled={refreshing || status === 'loading'}
                                onClick={handleRefresh}
                            >
                                <Icon
                                    icon={
                                        refreshing
                                            ? 'line-md:loading-loop'
                                            : 'mdi:refresh'
                                    }
                                    width={16}
                                />
                                Refresh
                            </button>
                        ) : null}
                        {reportsAccess.canEdit ? (
                            <button
                                type="button"
                                className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                onClick={handleDelete}
                            >
                                <Icon icon="mdi:trash-can-outline" width={16} />
                                Delete
                            </button>
                        ) : null}
                    </div>
                </div>

                {snapshot ? (
                    <div className={styles.summaryRow}>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>Members</div>
                            <div className={styles.summaryValue}>
                                {snapshot.memberCount ?? rows.length}
                            </div>
                        </div>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>
                                Total logged
                            </div>
                            <div className={styles.summaryValue}>
                                {formatLogDuration(snapshot.totalLog)}
                            </div>
                        </div>
                    </div>
                ) : null}
            </header>

            <div className={styles.content}>
                {status === 'loading' ? (
                    <div className={styles.centered}>
                        <Icon
                            icon="line-md:loading-loop"
                            style={{ fontSize: 40, color: '#6366f1' }}
                        />
                    </div>
                ) : status === 'error' ? (
                    <div className={styles.centered}>
                        <Text type="danger">{error}</Text>
                        <button
                            type="button"
                            className={styles.loadMoreBtn}
                            onClick={loadReport}
                        >
                            Try again
                        </button>
                    </div>
                ) : (
                    <div className={styles.detailLayout}>
                        <div className={styles.tableWrap}>
                            <Table
                                rowKey={(r) => r.userId || r.email || r.name}
                                columns={columns}
                                dataSource={rows}
                                pagination={false}
                                onRow={(record) => ({
                                    onClick: () =>
                                        setExpandedUserId((prev) =>
                                            prev === record.userId
                                                ? null
                                                : record.userId
                                        ),
                                    style: { cursor: 'pointer' },
                                })}
                                locale={{
                                    emptyText: (
                                        <div className={styles.empty}>
                                            <p>No members in this snapshot</p>
                                        </div>
                                    ),
                                }}
                            />
                        </div>

                        {expandedRow ? (
                            <aside className={styles.detailSide}>
                                <div className={styles.detailSideHeader}>
                                    <div>
                                        <div className={styles.userName}>
                                            {expandedRow.name || 'Member'}
                                        </div>
                                        <div className={styles.userEmail}>
                                            {formatLogDurationDetailed(
                                                expandedRow.totalLog
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.iconBtn}
                                        onClick={() => setExpandedUserId(null)}
                                        aria-label="Close detail"
                                    >
                                        <Icon icon="mdi:close" width={16} />
                                    </button>
                                </div>
                                <div className={styles.daysList}>
                                    {(expandedRow.days || []).length === 0 ? (
                                        <p className={styles.emptyDay}>
                                            No day breakdown in range
                                        </p>
                                    ) : (
                                        expandedRow.days.map((day) => (
                                            <div
                                                key={day.date}
                                                className={styles.dayBlock}
                                            >
                                                <div className={styles.dayHead}>
                                                    <span>
                                                        {formatReportingDisplayDate(
                                                            day.date
                                                        )}
                                                    </span>
                                                    <span>
                                                        {formatLogDuration(
                                                            day.totalLog
                                                        )}
                                                    </span>
                                                </div>
                                                {(day.projects || []).map(
                                                    (project) => (
                                                        <div
                                                            key={
                                                                project.projectId
                                                            }
                                                            className={
                                                                styles.projectBlock
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.projectHead
                                                                }
                                                            >
                                                                <span>
                                                                    {project.name ||
                                                                        'Project'}
                                                                </span>
                                                                <span>
                                                                    {formatLogDuration(
                                                                        project.totalLog
                                                                    )}
                                                                </span>
                                                            </div>
                                                            {(
                                                                project.tasks ||
                                                                []
                                                            ).map((task) => (
                                                                <div
                                                                    key={
                                                                        task.taskId
                                                                    }
                                                                    className={
                                                                        styles.taskRow
                                                                    }
                                                                >
                                                                    <span>
                                                                        {task.title ||
                                                                            'Task'}
                                                                    </span>
                                                                    <span>
                                                                        {formatLogDuration(
                                                                            task.totalLog
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </aside>
                        ) : null}
                    </div>
                )}
            </div>

            <StoryMapDeleteConfirm
                open={deleteOpen}
                title="Delete report?"
                message={`"${report?.name || 'This report'}" will be removed from Saved reports.`}
                centerActions
                onCancel={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    )
}

export default SavedReportDetailPage
